'use client';

import { useTranslation } from '@whoop/i18n/lang/client';
import { CarouselItem } from 'ui/components/Carousel/Carousel';
import { CarouselWithDots } from 'ui/components/CarouselWithDots/CarouselWithDots';
import { WhatsIncludedTile } from './WhatsIncludedTile';
import { WhatsIncludedTierInfo } from '@/types/maverickTypes';
import {
  MembershipTier,
  MembershipTierWithTrial,
} from '@/lib/constants/maverick';
import { PillGroup } from 'ui/components/PillGroup/PillGroup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import SectionHeader from '@/components/SectionHeader/SectionHeader';
import { tierLogoMap } from '@/lib/constants/images';

export interface WhatsIncludedProps {
  whatsIncludedInfo: WhatsIncludedTierInfo[];
  softSelectedTier: MembershipTier;
}

// Content is coming from Contentful, thus should already be translated
export function WhatsIncludedCarousel({
  whatsIncludedInfo,
  softSelectedTier,
}: WhatsIncludedProps): JSX.Element | null {
  const { t } = useTranslation('membershipTiers');

  const [selectedTier, setSelectedTier] =
    useState<MembershipTierWithTrial>(softSelectedTier);

  useEffect(() => {
    setSelectedTier(softSelectedTier);
  }, [softSelectedTier]);

  const getCalculatedStartIndex = useCallback(() => {
    const softSelectedTierIndex =
      whatsIncludedInfo?.findIndex((info) => info.tier === softSelectedTier) ??
      0;

    return softSelectedTierIndex < 0 ? 0 : softSelectedTierIndex;
  }, [softSelectedTier]);

  const whatsIncludedTiles = useMemo(() => {
    return whatsIncludedInfo?.map((whatsIncludedTierInfo) => {
      return {
        component: (
          <WhatsIncludedTile
            tier={whatsIncludedTierInfo.tier}
            logoUrl={tierLogoMap[whatsIncludedTierInfo.tier]}
            description={whatsIncludedTierInfo.description}
            url={whatsIncludedTierInfo.imageCollection.items[0].url}
          />
        ),
        tier: whatsIncludedTierInfo.tier,
      };
    });
  }, [whatsIncludedInfo]);

  const selectedWhatsIncludedTile = useMemo(() => {
    if (!whatsIncludedTiles || whatsIncludedTiles.length < 1) return null;
    return (
      whatsIncludedTiles.find((tile) => tile.tier === selectedTier)
        ?.component ?? whatsIncludedTiles[0].component
    );
  }, [selectedTier, whatsIncludedTiles]);

  const handlePillClick = (tier: MembershipTierWithTrial) => {
    setSelectedTier(tier);
  };

  return (
    <div className='bg-gray-50 px-6 py-14'>
      <div className='md:flex md:flex-col md:items-center md:px-16'>
        <div className='grid grid-cols-3 justify-between md:w-full md:max-w-screen-lg'>
          <SectionHeader className='col-span-3 pb-6 text-left md:col-span-2'>
            {t('whats-included')}
          </SectionHeader>
          <div className='items col-span-1 hidden md:flex'>
            <PillGroup
              categories={whatsIncludedInfo.map((info) => {
                return { categoryKey: info.tier, translatedName: info.tier };
              })}
              onClick={(tier) =>
                handlePillClick(tier as MembershipTierWithTrial)
              }
              externalSelectedKey={selectedTier}
              className='w-full pt-2'
              buttonClassName='rounded-2xl'
            />
          </div>
        </div>

        <div className='flex justify-center md:w-full'>
          <div
            className='hidden w-full justify-center gap-8 md:flex'
            data-testid='whats-included-selected-tile'
          >
            {selectedWhatsIncludedTile}
          </div>
          <div className='overflow-hidden  md:hidden'>
            <CarouselWithDots
              showSneakPeek={true}
              setIndexFocused={() => {}}
              startIndex={getCalculatedStartIndex()}
            >
              {whatsIncludedTiles?.map((tile) => (
                <CarouselItem
                  key={`whats-included-tile-${tile.tier}`}
                  className='flex w-full basis-full justify-center'
                >
                  {tile.component}
                </CarouselItem>
              ))}
            </CarouselWithDots>
          </div>
        </div>
      </div>
    </div>
  );
}
